import { Component } from 'react'
import RDGateway from '../../gateways/RDGateway'

/**
 * RD Station WhatsApp Form Tracker
 *
 * This component monitors for the RD Station WhatsApp popup widget and:
 * 1. Captures form submissions to send to our RD Gateway (same as DemoForm)
 * 2. Intercepts and modifies the WhatsApp message based on the "atividade da empresa" field
 *
 * Just mount this component anywhere on the page - it renders nothing but listens
 * for form submissions from the RD Station popup.
 */
class RDWhatsappFormTracker extends Component {
    constructor(props) {
        super(props)
        this.rdGateway = new RDGateway()
        this.handleFormSubmit = this.handleFormSubmit.bind(this)
        this.handleButtonClick = this.handleButtonClick.bind(this)
        this.handleLinkClick = this.handleLinkClick.bind(this)
        this.handleFormChange = this.handleFormChange.bind(this)
        this.interceptWhatsAppMessage = this.interceptWhatsAppMessage.bind(this)
        this.lastWhatsAppMessage = null
        this.popupObserver = null
        this.popupActionCache = new WeakMap()
    }

    componentDidMount() {
        document.addEventListener('submit', this.handleFormSubmit, true)
        document.addEventListener('click', this.handleButtonClick, true)
        document.addEventListener('click', this.handleLinkClick, true)
        document.addEventListener('change', this.handleFormChange, true)
        if (typeof window !== 'undefined') {
            window.__mercosOpenRDWhatsappLauncher = () => this.openLauncher()
        }
        this.patchWindowOpen()
        this.patchLocation()
        this.startPopupObserver()
    }

    componentWillUnmount() {
        document.removeEventListener('submit', this.handleFormSubmit, true)
        document.removeEventListener('click', this.handleButtonClick, true)
        document.removeEventListener('click', this.handleLinkClick, true)
        document.removeEventListener('change', this.handleFormChange, true)
        if (typeof window !== 'undefined' && window.__mercosOpenRDWhatsappLauncher) {
            delete window.__mercosOpenRDWhatsappLauncher
        }
        this.unpatchWindowOpen()
        this.unpatchLocation()
        this.stopPopupObserver()
    }

    patchWindowOpen() {
        if (typeof window === 'undefined') return
        if (window.__rdWhatsappOpenPatched) return
        window.__rdWhatsappOpenPatched = true
        window.__rdWhatsappOriginalOpen = window.open
        window.open = (url, target, features) => {
            const fixedUrl = this.fixWhatsappUrl(url, this.lastWhatsAppMessage)
            // Open WhatsApp in the same tab instead of a new tab
            // This improves conversion rate by keeping user focus
            if (fixedUrl && (fixedUrl.includes('wa.me') || fixedUrl.includes('whatsapp.com'))) {
                window.location.href = fixedUrl
                return null
            }
            return window.__rdWhatsappOriginalOpen.call(window, fixedUrl, target, features)
        }
    }

    unpatchWindowOpen() {
        if (typeof window === 'undefined') return
        if (!window.__rdWhatsappOpenPatched) return
        window.open = window.__rdWhatsappOriginalOpen
        delete window.__rdWhatsappOriginalOpen
        delete window.__rdWhatsappOpenPatched
    }

    patchLocation() {
        if (typeof window === 'undefined') return
        if (window.__rdWhatsappLocationPatched) return
        const location = window.location
        const originalAssign = location.assign ? location.assign.bind(location) : null
        const originalReplace = location.replace ? location.replace.bind(location) : null
        if (!originalAssign && !originalReplace) return
        // Check if we can override location methods (some browsers disallow this)
        const locationDescriptor = Object.getOwnPropertyDescriptor(window.location, 'assign')
        if (locationDescriptor && !locationDescriptor.writable) {
            // Can't patch, silently skip
            return
        }
        try {
            if (originalAssign) {
                location.assign = (url) => {
                    const fixedUrl = this.fixWhatsappUrl(url, this.lastWhatsAppMessage)
                    return originalAssign(fixedUrl)
                }
                window.__rdWhatsappOriginalAssign = originalAssign
            }
            if (originalReplace) {
                location.replace = (url) => {
                    const fixedUrl = this.fixWhatsappUrl(url, this.lastWhatsAppMessage)
                    return originalReplace(fixedUrl)
                }
                window.__rdWhatsappOriginalReplace = originalReplace
            }
            window.__rdWhatsappLocationPatched = true
        } catch (error) {
            // Some browsers disallow overriding location methods
            // Silently skip - not critical functionality
        }
    }

    unpatchLocation() {
        if (typeof window === 'undefined') return
        if (!window.__rdWhatsappLocationPatched) return
        if (window.__rdWhatsappOriginalAssign) {
            window.location.assign = window.__rdWhatsappOriginalAssign
            delete window.__rdWhatsappOriginalAssign
        }
        if (window.__rdWhatsappOriginalReplace) {
            window.location.replace = window.__rdWhatsappOriginalReplace
            delete window.__rdWhatsappOriginalReplace
        }
        delete window.__rdWhatsappLocationPatched
    }

    fixWhatsappUrl(url, messageOverride) {
        if (!url || typeof url !== 'string') return url
        if (!url.includes('wa.me') && !url.includes('whatsapp.com')) return url
        try {
            const parsed = new URL(url, window.location.origin)
            const isWebWhatsapp = parsed.hostname === 'web.whatsapp.com' || parsed.hostname === 'whatsapp.com'
            const isWaMe = parsed.hostname === 'wa.me'

            let phone = parsed.searchParams.get('phone')
            const text = messageOverride || parsed.searchParams.get('text') || ''

            let finalUrl = url

            if (isWebWhatsapp) {
                // Convert web.whatsapp.com/send?phone=X to wa.me/X format
                // wa.me format: https://wa.me/55XXXXXXXXXXX?text=MESSAGE (phone in path, not query param)
                if (phone) {
                    const cleanPhone = phone.replace(/[^\d]/g, '') // Remove +, -, (, ), spaces
                    const urlObj = new URL(`https://wa.me/${cleanPhone}`)
                    if (text) {
                        urlObj.searchParams.set('text', text)
                    }
                    finalUrl = urlObj.toString()
                    console.log('RD WhatsApp Tracker: Converted web.whatsapp.com to wa.me', url, '->', finalUrl)
                }
            } else if (isWaMe) {
                // Already wa.me format, just update text if needed
                const cleanPhone = phone ? phone.replace(/[^\d]/g, '') : parsed.pathname.replace(/^\/|\/$/g, '')
                const urlObj = new URL(`https://wa.me/${cleanPhone}`)
                if (text) {
                    urlObj.searchParams.set('text', text)
                }
                finalUrl = urlObj.toString()
            } else if (messageOverride) {
                parsed.searchParams.set('text', messageOverride)
                finalUrl = parsed.toString()
            }

            return finalUrl
        } catch (error) {
            console.warn('RD WhatsApp Tracker: Failed to fix WhatsApp URL', error)
            return url
        }
    }

    isRDStationPopupForm(form) {
        return form.classList.contains('rdstation-popup-js-form-identifier') ||
               form.closest('[id*="bricks-component"]')
    }

    extractFormData(form) {
        const nameInput = form.querySelector('input[name="name"], .js-field-name')
        const emailInput = form.querySelector('input[name="email"], .js-field-email')
        const phoneInput = form.querySelector('input[name="personal_phone"], input[name="mobile_phone"], .phone')
        const activityInput = form.querySelector('select[name="custom_fields[242]"], .js-field-cf_atividade_da_empresa')
        const sellersInput = form.querySelector('select[name="custom_fields[247]"], .js-field-cf_numero_de_vendedores')
        const clientSelect = form.querySelector('select[name="custom_fields[cf_cliente]"], select[name="cf_cliente"], .js-field-cf_cliente')
        const clientChecked = form.querySelector('input[name="custom_fields[cf_cliente]"]:checked, input[name="cf_cliente"]:checked')
        let clientValue = ''
        if (clientChecked) {
            clientValue = clientChecked.value
        } else if (clientSelect) {
            if (clientSelect.matches('select, input, textarea')) {
                clientValue = clientSelect.value
            } else {
                const nestedInput = clientSelect.querySelector('input:checked, select')
                clientValue = nestedInput ? nestedInput.value : ''
            }
        }

        return {
            name: nameInput ? nameInput.value.trim() : '',
            email: emailInput ? emailInput.value.trim() : '',
            telephone: phoneInput ? phoneInput.value.trim() : '',
            activity: activityInput ? activityInput.value : '',
            sellers: sellersInput ? sellersInput.value : '',
            client: clientValue
        }
    }

    /**
     * Decode base64 string (ASCII-safe JSON)
     */
    decodeBase64(base64String) {
        try {
            // Handle URL-safe base64 and remove newlines
            let normalized = base64String.replace(/-/g, '+').replace(/_/g, '/').replace(/[\n\r]/g, '')
            const padding = normalized.length % 4
            if (padding) {
                normalized += '='.repeat(4 - padding)
            }
            return atob(normalized)
        } catch (error) {
            console.error('RD WhatsApp Tracker: Failed to decode base64', error)
            return null
        }
    }

    /**
     * Encode to base64 string (ASCII-safe JSON)
     */
    encodeBase64(str) {
        try {
            const asciiSafe = str.replace(/[\u0080-\uFFFF]/g, (char) => {
                return `\\u${char.charCodeAt(0).toString(16).padStart(4, '0')}`
            })
            const encoded = btoa(asciiSafe)
            return encoded.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
        } catch (error) {
            console.error('RD WhatsApp Tracker: Failed to encode base64', error)
            return null
        }
    }

    /**
     * Get the appropriate WhatsApp message based on activity
     */
    normalizeValue(value) {
        return (value || '')
            .toString()
            .trim()
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
    }

    buildIdentity(formData) {
        const name = formData.name ? formData.name.trim() : ''
        const email = formData.email ? formData.email.trim() : ''
        if (!name && !email) return ''
        if (!name) return `Meu email é ${email}`
        if (!email) return `Meu nome é ${name}`
        return `Meu nome é ${name} e meu email é ${email}`
    }

    getWhatsAppMessage(formData) {
        const normalizedActivity = this.normalizeValue(formData.activity)
        const normalizedClient = this.normalizeValue(formData.client)
        const isClient = normalizedClient === 'sim'
        const isNotClient = normalizedClient === 'nao'
        const isIndustry = normalizedActivity === 'industria'
        const isDistribution = normalizedActivity === 'distribuicao'
        const isRep = normalizedActivity === 'representacao comercial'
        const identity = this.buildIdentity(formData)

        if (isClient && (isIndustry || isDistribution || isRep)) {
            return `Olá. Sou cliente Mercos e gostaria de atendimento. ${identity}`.trim()
        }

        if (isNotClient && isRep) {
            return `Olá. Sou Representante e acabei de solicitar uma demonstração do Mercos. ${identity}`.trim()
        }

        if (isNotClient && (isIndustry || isDistribution)) {
            return `Olá. Ainda não sou cliente Mercos e acabei de solicitar uma demonstração. ${identity}`.trim()
        }

        if (identity) {
            return `Olá, acabei de solicitar uma demonstração do Mercos e gostaria de mais detalhes. ${identity}`.trim()
        }

        return 'Olá, acabei de solicitar uma demonstração do Mercos e gostaria de mais detalhes.'
    }

    updateLastMessage(form) {
        const formData = this.extractFormData(form)
        const newMessage = this.getWhatsAppMessage(formData)
        this.lastWhatsAppMessage = newMessage
        return { formData, newMessage }
    }

    updateWhatsAppLinks(container, message) {
        if (!message || typeof document === 'undefined') return
        const root = container || document
        const links = root.querySelectorAll('a[href*="wa.me"], a[href*="whatsapp.com"]')
        console.log('RD WhatsApp Tracker: updateWhatsAppLinks found', links.length, 'WhatsApp links')
        links.forEach((link, index) => {
            const originalHref = link.href
            const newHref = this.fixWhatsappUrl(link.href, message)
            console.log(`RD WhatsApp Tracker: Link ${index}`, { original: originalHref, modified: newHref })
            if (newHref && newHref !== link.href) {
                link.href = newHref
            }
        })
    }

    /**
     * Intercept and modify the WhatsApp message in data-popup-action
     */
    interceptWhatsAppMessage(form) {
        const popupActionAttr = form.getAttribute('data-popup-action')
        if (!popupActionAttr) {
            console.warn('RD WhatsApp Tracker: No data-popup-action found')
            return false
        }

        const decoded = this.decodeBase64(popupActionAttr)
        if (!decoded) return false

        let actionData
        try {
            actionData = JSON.parse(decoded)
        } catch (error) {
            console.error('RD WhatsApp Tracker: Failed to parse action data', error)
            return false
        }

        if (!actionData.metadata || !actionData.metadata.message) {
            console.warn('RD WhatsApp Tracker: No message in metadata')
            return false
        }

        const { formData, newMessage } = this.updateLastMessage(form)

        // Log before modification
        console.log('RD WhatsApp Tracker: Activity:', formData.activity)
        console.log('RD WhatsApp Tracker: Original message:', actionData.metadata.message)

        // Update the message
        actionData.metadata.message = newMessage

        // Re-encode and update the attribute
        const newEncoded = this.encodeBase64(JSON.stringify(actionData))
        if (!newEncoded) return false

        if (popupActionAttr === newEncoded || this.popupActionCache.get(form) === newEncoded) {
            return true
        }

        form.setAttribute('data-popup-action', newEncoded)
        this.popupActionCache.set(form, newEncoded)

        console.log('RD WhatsApp Tracker: Modified message:', newMessage)
        console.log('RD WhatsApp Tracker: data-popup-action updated successfully')

        return true
    }

    sendTrackingData(formData) {
        if (!formData.email) {
            console.warn('RD WhatsApp Tracker: No email found, skipping tracking')
            return
        }

        const data = {
            conversion_identifier: 'solicitou-demonstracao-widget-whatsapp',
            'cf_tipo_de_conversao': 'Solicitou demonstração via widget Whatsapp',
            'cf_atividade_da_empresa': formData.activity,
            'cf_numero_de_vendedores': formData.sellers,
            'cf_cliente': formData.client,
            company_name: formData.name + ' - ' + formData.email,
            name: formData.name,
            email: formData.email,
            personal_phone: formData.telephone,
            event_category: 'rd_whatsapp_popup',
            event_action: 'solicitou_demo',
            event_label: 'forca_de_vendas',
        }

        // Use RDGateway - it handles cookies, hashing, dataLayer, RD Station API, and Hubspot
        // Pass null for redirect since we don't want to redirect
        this.rdGateway.sendToRDStation(data, null)

        console.log('RD WhatsApp Tracker: Data sent via RDGateway', data)
    }

    handleFormSubmit(event) {
        const form = event.target.closest ? event.target.closest('form') : event.target
        if (!form || form.tagName !== 'FORM') return

        if (!this.isRDStationPopupForm(form)) return

        const { formData, newMessage } = this.updateLastMessage(form)

        // Intercept and modify WhatsApp message BEFORE submission
        this.interceptWhatsAppMessage(form)
        this.updateWhatsAppLinks(form, newMessage)

        console.log('RD WhatsApp Tracker: Captured form submit', formData)
        this.sendTrackingData(formData)
    }

    handleButtonClick(event) {
        const button = event.target.closest('button[type="submit"], input[type="submit"], .rdstation-popup-js-submit-button')
        if (!button) return

        const form = button.closest('form')
        if (!form) return

        if (!this.isRDStationPopupForm(form)) return

        const { formData, newMessage } = this.updateLastMessage(form)

        // Intercept and modify WhatsApp message BEFORE click processing
        this.interceptWhatsAppMessage(form)
        this.updateWhatsAppLinks(form, newMessage)

        console.log('RD WhatsApp Tracker: Captured button click', formData)
        this.sendTrackingData(formData)
    }

    handleLinkClick(event) {
        const link = event.target.closest && event.target.closest('a[href*="wa.me"], a[href*="whatsapp.com"]')
        if (!link) return
        const form = link.closest('form')
        let message = this.lastWhatsAppMessage
        if (form && this.isRDStationPopupForm(form)) {
            const { newMessage } = this.updateLastMessage(form)
            message = newMessage
        }
        if (!message) return
        const newHref = this.fixWhatsappUrl(link.href, message)
        if (newHref && newHref !== link.href) {
            link.href = newHref
        }
        // Remove target="_blank" to open WhatsApp in same tab
        if (link.target === '_blank') {
            link.removeAttribute('target')
        }
    }

    handleFormChange(event) {
        const field = event.target
        if (!field || !field.closest) return
        if (!field.matches('select[name="custom_fields[242]"], .js-field-cf_atividade_da_empresa, select[name="custom_fields[cf_cliente]"], select[name="cf_cliente"], .js-field-cf_cliente, input[name="custom_fields[cf_cliente]"], input[name="cf_cliente"]')) return
        const form = field.closest('form')
        if (!form || !this.isRDStationPopupForm(form)) return
        const { newMessage } = this.updateLastMessage(form)
        this.interceptWhatsAppMessage(form)
        this.updateWhatsAppLinks(form, newMessage)
    }

    startPopupObserver() {
        if (typeof MutationObserver === 'undefined' || typeof document === 'undefined') return
        if (this.popupObserver) return
        this.popupObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'data-popup-action') {
                    const form = mutation.target
                    if (form && form.tagName === 'FORM' && this.isRDStationPopupForm(form)) {
                        const currentValue = form.getAttribute('data-popup-action')
                        if (currentValue && this.popupActionCache.get(form) === currentValue) {
                            return
                        }
                        const { newMessage } = this.updateLastMessage(form)
                        this.interceptWhatsAppMessage(form)
                        this.updateWhatsAppLinks(form, newMessage)
                    }
                }
            })
        })
        this.popupObserver.observe(document.documentElement, {
            subtree: true,
            attributes: true,
            attributeFilter: ['data-popup-action'],
        })
    }

    stopPopupObserver() {
        if (!this.popupObserver) return
        this.popupObserver.disconnect()
        this.popupObserver = null
    }

    getLauncherButton() {
        if (typeof document === 'undefined') return null
        return document.querySelector('.bricks--floating--button.rdstation-popup-js-floating-button')
    }

    openLauncher() {
        const button = this.getLauncherButton()
        if (!button) return false
        button.click()
        return true
    }

    render() {
        return null
    }
}

export default RDWhatsappFormTracker



// WEBPACK FOOTER //
// /usr/src/site-blog/src/components/site/RDWhatsappFormTracker.jsx